import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ensureCsrfCookie } from '../../api/client';
import { useEffect } from 'react';
import { zapomnitPodpisiRoley } from './roleLabels';
import { zapomnitDenX } from './denX';
import { zasevFiliala } from '../multi-tenant/store';
import { ochistitDannye, priSmenePolzovatelya, ubratLichnoe } from './smenaPolzovatelya';

export interface AuthAccount {
  id: number;
  name: string;
  slug: string;
}

export interface AuthUser {
  id: number;
  /** Почта необязательна: приглашённый по СМС входит по телефону. */
  email: string | null;
  name: string;
  /** Канонический вид 79XXXXXXXXX; null, пока человек не вписал номер. */
  phone?: string | null;
  /** Готовая ссылка на фотографию либо null — тогда рисуются инициалы. */
  avatar_url?: string | null;
  roles: string[];
  /** Человеческие подписи ролей — из базы, а не из зашитого списка (`prv3`, §31). */
  role_labels?: Record<string, string>;
  permissions: string[];
  /**
   * Ключи разделов, спрятанных от этого человека (`prv3`). Слой видимости лежит
   * ПОВЕРХ прав: раздел может быть виден и всё равно закрыт правом, и наоборот.
   * Сервер закрывает их адреса сам — здесь они нужны, чтобы убрать пункт из меню
   * и не пускать по прямой ссылке.
   */
  hidden_sections?: string[];
  /**
   * Как этому человеку показывать номер клиентки (`s318`): открыт всегда ·
   * скрыт и открывается кликом со следом · наглухо. Настоящий замок — на
   * сервере; экран лишь не зовёт нажимать на то, что откажет.
   */
  rezhim_nomera?: 'otkryt' | 'po_kliku' | 'naglukho';
  two_factor_enabled: boolean;
  two_factor_required_setup?: boolean;
  account_id: number | null;
  account: AuthAccount | null;
  /**
   * ⛔ `s320` Д3/Д47: ТЕКУЩИЙ ФИЛИАЛ ЧЕЛОВЕКА — ОДНА ВЕЛИЧИНА НА ВСЮ ПАНЕЛЬ.
   * Приезжает с сервера и он же старше памяти браузера: пока филиал жил только
   * в `localStorage`, переключатель говорил «Лэш СПб», а экран отдавал Москву.
   * `null` — осознанная «вся сеть».
   */
  tekushchiy_filial_id?: number | null;
  /** Куда вести человека сразу после входа — решает роль (экран «Вход и доступ»). */
  start_screen?: string;
  /** Можно ли отсюда войти под другим пользователем (право users.impersonate). */
  can_impersonate?: boolean;
  /** Кто на самом деле сидит за сессией, если идёт просмотр под чужой личиной. */
  impersonated_by?: { id: number; name: string } | null;
  /** s966: настал ли день X (УС отключена) — кнопки и ссылки УС снимаются с экрана. */
  den_x?: boolean;
}

/** Строка переключателя «войти как» — лёгкий список кандидатов. */
export interface ImpersonatableUser {
  id: number;
  name: string;
  email: string | null;
  /** Человеческий вид номера; показывается вместо почты, когда её нет. */
  phone?: string | null;
  avatar_url?: string | null;
  roles: string[];
  account_id: number | null;
  last_login_at: string | null;
}

export interface TwoFactorChallenge {
  type: '2fa_required';
  challenge_id: string;
  expires_at: string;
}

export type LoginResult = AuthUser | TwoFactorChallenge;

export function isTwoFactorChallenge(r: LoginResult): r is TwoFactorChallenge {
  return (r as TwoFactorChallenge).type === '2fa_required';
}

export interface LoginPayload {
  /** Почта или телефон — одно поле, форму записи разбирает бэкенд. */
  login: string;
  password: string;
  remember?: boolean;
}

const ME_QUERY_KEY = ['auth', 'me'] as const;

export function useMe() {
  return useQuery<AuthUser | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await apiClient.get('/auth/me');
        zapomnitDenX((data.data as AuthUser | null)?.den_x);
        return data.data as AuthUser;
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
    // ⛔ Права, выданные владельцем, обязаны доехать до ОТКРЫТОЙ панели сами.
    // Глобально `refetchOnWindowFocus` выключен, и список прав снимался один раз
    // на входе: менеджеру открыли «Обращения» и чат, а её панель, открытая с
    // утра, их не показывала до F5 (владелец, 24.09.2026).
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LoginPayload): Promise<LoginResult> => {
      await ensureCsrfCookie();
      const { data } = await apiClient.post('/auth/login', payload);
      return data.data as LoginResult;
    },
    onSuccess: (result) => {
      // Только не-challenge результат — это полный AuthUser, кладём в кэш.
      if (!isTwoFactorChallenge(result)) {
        zapomnitDenX(result.den_x);
        qc.setQueryData(ME_QUERY_KEY, result);
      }
    },
  });
}

export function useVerifyTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { challenge_id: string; code: string }) => {
      await ensureCsrfCookie();
      const { data } = await apiClient.post('/auth/2fa/verify', args);
      return data.data as { logged_in: boolean; recovery_code_used: boolean };
    },
    onSuccess: () => {
      // После успешного verify — рефетчим /auth/me чтобы загрузить payload.
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // `s719`: аренды отпускаются и запросы отменяются, пока сессия ещё этого человека.
      await priSmenePolzovatelya(qc, () => apiClient.post('/auth/logout'));
    },
    onSuccess: () => {
      ubratLichnoe();
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.clear();
    },
  });
}

/**
 * Кандидаты для просмотра «глазами пользователя». Запрос уходит только когда
 * переключатель реально открыт (`enabled`) — под чужой личиной эндпоинт закрыт.
 */
export function useImpersonatableUsers(enabled: boolean) {
  return useQuery<ImpersonatableUser[]>({
    queryKey: ['auth', 'impersonate', 'users'],
    queryFn: async () => {
      const { data } = await apiClient.get('/auth/impersonate/users');
      return data.data as ImpersonatableUser[];
    },
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Переключение личины: пароль не участвует — сервер меняет пользователя в самой
 * сессии. Весь кэш сбрасывается целиком: данные предыдущего пользователя под
 * новыми правами показывать нельзя (§ права читаются с каждым запросом).
 */
export function useImpersonate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      await priSmenePolzovatelya(qc, () => apiClient.post(`/auth/impersonate/${userId}`));
    },
    onSuccess: () => {
      ochistitDannye(qc);
    },
  });
}

/** Возврат к себе. Кэш так же сбрасывается целиком. */
export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await priSmenePolzovatelya(qc, () => apiClient.delete('/auth/impersonate'));
    },
    onSuccess: () => {
      ochistitDannye(qc);
    },
  });
}

export function useAuth() {
  const me = useMe();
  // ⛔ `prv3`: подписи ролей уезжают в общий кэш модуля, чтобы бейдж роли мог назвать
  // по-человечески и роль, заведённую владельцем на экране «Роли», — не таща в каждый
  // крошечный бейдж провайдер запросов (op-rules §31).
  zapomnitPodpisiRoley(me.data?.role_labels);
  /*
   * ⛔ `s320` Д3: ПЕРЕКЛЮЧАТЕЛЬ ПОКАЗЫВАЕТ ТО, ПО ЧЕМУ СЕРВЕР ФИЛЬТРУЕТ.
   * Владелец при входе видел «Все филиалы», хотя работает в Москве: память
   * браузера была пуста, а серверный филиал никто не спрашивал. Значение
   * сервера кладётся в store на входе — один раз, и только если человек в этой
   * сессии переключателя ещё не касался.
   */
  // Засев — в эффекте, а не в рендере: правка стороннего store прямо по ходу
  // отрисовки роняет предупреждение React и ломает порядок обновлений.
  const serverFilial = me.data?.tekushchiy_filial_id ?? null;
  const gotov = !!me.data;
  useEffect(() => {
    zasevFiliala(serverFilial, gotov);
  }, [serverFilial, gotov]);

  return {
    user: me.data ?? null,
    isLoading: me.isLoading,
    isAuthenticated: !!me.data,
    refetch: me.refetch,
  };
}
